import { inject, observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import Box from "react-raster";
import { match } from "react-router";
import { Button, Heading, Paper, Toggle } from "src/components";
import { theme } from "src/config";
import { Space } from "src/models";
import { MatchParams } from "src/routes";
import { AuthStore, RoutingStore, UserStore } from "src/stores";
import { QuestionStore } from "src/stores/questionStore";
import { SpaceStore } from "src/stores/spaceStore";
import { DataType, SpaceType } from "src/types";
import { Page } from "./../routes";

export interface IHomeProps {
  authStore?:     AuthStore;
  userStore?:     UserStore;
  routingStore?:  RoutingStore;
  spaceStore?:    SpaceStore;
  questionStore?: QuestionStore;
  match:          match<MatchParams>;  // Routing parameters. This is injected so it has to be called match
}

export const Home = inject("authStore", "userStore", "routingStore", "spaceStore", "questionStore")(observer((props: IHomeProps): JSX.Element => {
    const { t, i18n } = useTranslation();

    const { logout, isLoggedIn, getCurUser: user } = props.authStore;

    const handleClick = async () => {
      if (!isLoggedIn) {
        return props.routingStore.push(`/${Page.Login}`);
      }
      const success = await props.authStore.logout();
      props.routingStore.push(`/${Page.Login}`);
    };
    interface INode {value: Space; children?: INode[]};
    
    const tree: INode[] = props.spaceStore.getItemsByType(SpaceType.Workspace).map(w => {
        const projects = props.spaceStore.getItemsByParent(w.id, SpaceType.Project);
        return {
          "value": w,
          "children": projects.map(p => {
            const rooms = props.spaceStore.getItemsByParent(p.id, SpaceType.Room);
            return {
              value: p,
              children:  rooms.map(r => ({value: r}))
            }
          }, {})
        }
    });

    return (
      <Paper style={theme.paper.regular}>
        <Box gridRowGap={theme.layout.gutter}>
          <Heading type="h3">{t("welcome")}</Heading>
          <Box>
            <hr />
          </Box>
          {tree.map(w => (
            <div key={w.value.id}>
              <span key={w.value.id+"span"} onClick={() => props.spaceStore.changeWorkspace(w.value.id)}>
                <strong>{w.value.title}</strong>
                {user.inSpace(w.value.id) ? (
                  <>
                  (role:{user.getRole(w.value.id)} status:{user.getStatus(w.value.id)})
                </>
                ) : ' Not active workspace'}
                </span>
              {w.children && w.children.map(p => (
                <div key={p.value.id}>
                  <span>&emsp;<strong>{p.value.title}</strong> (role:{user.getRole(p.value.id)} status:{user.getStatus(p.value.id)})</span><br/>
                  {p.children && p.children.map(r => (
                    <span key={r.value.id+"outer"}><span onClick={() => props.spaceStore.changeRoom(r.value.id)} key={r.value.id}>&emsp;&emsp;<strong>{r.value.title}</strong> (role:{user.getRole(r.value.id)} status:{user.getStatus(r.value.id)})</span><br/></span>
                  ))}
              </div>
            ))}
            </div>
          ))}
          <Button key="login" size="default" variant="primary" onClick={handleClick}>
            {isLoggedIn ? t("logout") : t("login")}
          </Button>
          <Toggle label="from" checked={props.questionStore.getDashboardFilterFrom} onChange={() => props.questionStore.toggleDashboardFilterFrom()}/>
          {props.questionStore.getItems.map(q => 
            <p key={q.id}>{q.id + ": " + q.title}</p>)
          }
          <Button key="options" size="default" variant="primary" onClick={() => {
            props.questionStore.getItems[0] && props.questionStore.getItems[0].loadItems(DataType.Question, true);
          }}>
            Show options
          </Button>
          <Button key="reset" size="default" variant="primary" onClick={() => {
            props.questionStore.getItems[0] && props.questionStore.getItems[0].resetPagination(DataType.Question, true);
          }}>
            Reset pagination
          </Button>
          <p>Options for {props.questionStore.getItems[0] && props.questionStore.getItems[0].id}</p>
          {props.questionStore.getItems[0] && props.questionStore.getItems[0].getOptions.map(q => 
            <p key={q.id}>{q.id + ": " + q.title}</p>)
          }
        </Box>
      </Paper>
    );
  })
);
